import { Box, Stack, Theme, Typography, useTheme } from '@mui/material';
import { Trans } from 'react-i18next';

import { ProgressDots } from '@/components/DataDisplay/Indicator/ProgressDots';
import { Icon } from '@/components/Icon/Icon';
import { Page } from '@/components/Layout/Page/Page';
import { BackgroundPaperProps } from '@/components/Layout/Page/Page.interface';
import { PageTitleContent } from '@/components/Layout/Page/Page.styled';

import { useAppSelector } from '@/hooks/redux';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';

import { BasicInfoForm } from './components/BasicInfoForm';

import { PageTitle } from './BasicInfoPage.styled';

export function BasicInfoPage() {
  const theme = useTheme();
  const { t, language } = useTranslate();
  const {
    brandingImageInfoPage,
    shouldShowLeftBrandingLogo,
    shouldShowRightBrandingLogo,
  } = useAppSelector(selectConfigsState);
  const { isMobile } = useResponsive();
  const paperProps: Partial<BackgroundPaperProps> = isMobile
    ? {}
    : {
        position: 'right',
        size: 'big',
        bgImg: brandingImageInfoPage,
        color: (theme: Theme) => theme.palette.overlay.darkBlue.start,
        secondColor: (theme: Theme) => theme.palette.overlay.darkBlue.end,
        hasLogo: shouldShowRightBrandingLogo,
        hasGradient: shouldShowRightBrandingLogo,
        children: isMobile ? null : (
          <>
            {!shouldShowRightBrandingLogo && <Box height={'100%'} />}
            <Stack direction="row" justifyContent="center" gap={1.5}>
              <Icon name="lock" color={theme => theme.palette.common.white} />
              <Typography
                variant="caption"
                color={theme.palette.common.white}
                {...getDataTestId('basic-info-page-caption')}
              >
                {t('createNewAccount.basicInfo.caption')}
              </Typography>
            </Stack>
          </>
        ),
      };

  return (
    <Page
      paperProps={{
        ...paperProps,
      }}
      progressProps={PROGRESS_STEPS.basicInfoStep}
      hasLogo={shouldShowLeftBrandingLogo}
      headerMobile={{
        centerSlot: <ProgressDots {...PROGRESS_STEPS.basicInfoStep} />,
        bgColor: (theme: Theme) => theme.palette.background.paper,
      }}
      formContainerProps={{
        sx: {
          [theme.breakpoints.up('lg')]: {
            maxHeight: `100vh`,
            marginTop: theme.spacing(4),
          },
        },
      }}
    >
      <Box pb={12} width="100%">
        <PageTitleContent>
          <Trans
            lang={language}
            i18nKey="createNewAccount.basicInfo.bodyDesktop"
            components={{
              br: <br />,
              a1: <PageTitle mb={6} />,
              a2: <PageTitle component="span" isHighlighted />,
            }}
          />
        </PageTitleContent>
        <BasicInfoForm />
      </Box>
    </Page>
  );
}

export default BasicInfoPage;
